import React, { useState, useRef, useEffect } from "react";
import ReactMarkdown from "react-markdown";

const AcademySection4 = ({ academy }) => {
  const [activeModule, setActiveModule] = useState(1);
  const buttonsContainerRef = useRef(null);
  const carouselRef = useRef(null);
  const [canScrollLeft, setCanScrollLeft] = useState(false);
  const [canScrollRight, setCanScrollRight] = useState(false);

  // Get available modules
  const availableModules = [];
  if (academy) {
    for (let i = 1; i <= 12; i++) {
      if (academy[`academyModule${i}`]) {
        availableModules.push(i);
      }
    }
  }

  // Check scroll position effect - placed before conditional return
  useEffect(() => {
    const checkScroll = () => {
      if (carouselRef.current) {
        const { scrollLeft, scrollWidth, clientWidth } = carouselRef.current;
        setCanScrollLeft(scrollLeft > 0);
        setCanScrollRight(scrollLeft + clientWidth < scrollWidth - 10);
      }
    };

    // Check initial scroll state
    checkScroll();

    // Add event listener
    const carousel = carouselRef.current;
    if (carousel) {
      carousel.addEventListener('scroll', checkScroll);
      return () => carousel.removeEventListener('scroll', checkScroll);
    }
  }, []);

  const handleModuleClick = (moduleIndex) => {
    setActiveModule(moduleIndex);
  };

  const scrollLeft = () => {
    if (carouselRef.current) {
      carouselRef.current.scrollBy({ left: -470, behavior: 'smooth' });
    }
  };

  const scrollRight = () => {
    if (carouselRef.current) {
      carouselRef.current.scrollBy({ left: 470, behavior: 'smooth' });
    }
  };

  // Early return if no data
  if (!academy || !academy.academyModule1) {
    return null;
  }

  const components = {
    h2: ({ children }) => (
      <h2 className="text-2xl font-semibold text-left text-[rgba(62,89,183,1)] mb-2">{children}</h2>
    ),
    h3: ({ children }) => (
      <h3 className="text-xl font-semibold text-gray-700 mb-4">{children}</h3>
    ),
    p: ({ children }) => (
      <p className="text-[#666666] mb-4 max-w-2xl text-sm mx-auto">{children}</p>
    ),
    ul: ({ children }) => (
      <ul className="list-disc pl-4 mb-6 text-gray-600 text-sm max-w-2xl mx-auto">{children}</ul>
    ),
    li: ({ children }) => <li className="mb-2">{children}</li>,
    br: () => <br />,
  };

  return (
    <div className="py-2 px-6 bg-white mt-10">
      <div className="container mx-auto">
        <div className="text-center">
          <h2 className="text-xl text-left font-bold text-[rgba(62,89,183,1)] mb-4">
            What your curriculum looks like
          </h2>
        </div>
        <div
          ref={buttonsContainerRef}
          className="flex flex-row space-x-2 overflow-x-auto pb-4 mb-4 scroll-smooth md:hidden"
        >
          {availableModules.map((moduleNum) => (
            <button
              key={moduleNum}
              onClick={() => handleModuleClick(moduleNum)}
              className={`px-4 py-2 rounded-full text-sm font-medium flex-none border 
                ${activeModule === moduleNum
                  ? "bg-[rgba(198,43,109,1)] text-white border-red-600"
                  : "bg-white text-[rgba(198,43,109,1)] border-[rgba(198,43,109,1)] hover:bg-gray-100"}`}
            >
              Module {moduleNum}
            </button>
          ))}
        </div>
        
        {/* Desktop view with carousel navigation */}
        <div className="hidden md:block relative">
          {/* Left Arrow */}
          <button 
            onClick={scrollLeft}
            className={`absolute left-0 top-1/2 -translate-y-1/2 z-10 bg-white rounded-full p-2 shadow-md -ml-4 
              ${canScrollLeft ? 'text-[rgba(62,89,183,1)]' : 'text-gray-300'}`}
            disabled={!canScrollLeft}
          >
            <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor" className="w-6 h-6">
              <path strokeLinecap="round" strokeLinejoin="round" d="M15.75 19.5L8.25 12l7.5-7.5" />
            </svg>
          </button>
          
          {/* Carousel */}
          <div 
            ref={carouselRef}
            className="flex space-x-4 overflow-x-auto pb-4 scrollbar-hide scroll-smooth"
          >
            {availableModules.map((moduleNum) => (
              <div
                key={moduleNum}
                className="w-[450px] bg-[rgba(236,238,248,1)] rounded-3xl p-6 flex-shrink-0"
              >
                <ReactMarkdown components={components}>
                  {academy[`academyModule${moduleNum}`]}
                </ReactMarkdown>
              </div>
            ))}
          </div>
          
          {/* Right Arrow */}
          <button 
            onClick={scrollRight}
            className={`absolute right-0 top-1/2 -translate-y-1/2 z-10 bg-white rounded-full p-2 shadow-md -mr-4
              ${canScrollRight ? 'text-[rgba(62,89,183,1)]' : 'text-gray-300'}`}
            disabled={!canScrollRight}
          >
            <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor" className="w-6 h-6">
              <path strokeLinecap="round" strokeLinejoin="round" d="M8.25 4.5l7.5 7.5-7.5 7.5" />
            </svg>
          </button>
        </div>
        
        {/* Mobile view */}
        <div className="w-full md:hidden">
          <div className="w-full bg-[rgba(236,238,248,1)] rounded-3xl p-6 overflow-y-auto">
            <ReactMarkdown components={components}>
              {academy[`academyModule${activeModule}`]}
            </ReactMarkdown>
          </div>
        </div>
      </div>
    </div>
  );
};

export default AcademySection4;